import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Container,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Box,
  useTheme,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tab,
  Tabs,
  useMediaQuery,
} from '@mui/material';
import {
  LogoutOutlined,
  PeopleOutlined,
  DescriptionOutlined,
  HomeOutlined,
  ReceiptOutlined,
  AccountBalanceWallet,
  StarOutline,
  WorkOutlineOutlined,
} from '@mui/icons-material';
import { useAuthStore } from '../stores/useAuthStore';
import SubscriptionBanner from './SubscriptionBanner';
import ProfileMenu from './ProfileMenu';

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user, logout } = useAuthStore();
  const [expensesDialogOpen, setExpensesDialogOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const hasPremiumAccess = user?.subscriptionTier === 'PREMIUM';
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 960px = mobile

  const getNavigationValue = () => {
    if (location.pathname.startsWith('/clients')) return 'clients';
    if (location.pathname.startsWith('/projets')) return 'projets';
    if (location.pathname.startsWith('/devis')) return 'devis';
    if (location.pathname.startsWith('/factures')) return 'factures';
    if (location.pathname.startsWith('/expenses')) return 'expenses';
    if (location.pathname === '/dashboard') return 'home';
    return 'home';
  };

  const handleNavigate = (value: string) => {
    // Gestion spéciale pour les dépenses : afficher la dialog Premium si pas Premium
    if (value === 'expenses' && !hasPremiumAccess) {
      setExpensesDialogOpen(true);
      return;
    }

    if (value === 'home') navigate('/dashboard');
    else navigate(`/${value}`);
  };

  return (
    <Box sx={{ pb: isMobile ? 7 : 0 }}>
      {/* Top AppBar - Navigation Desktop + Mobile */}
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: theme.palette.primary.main,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          {/* Logo / Nom de l'entreprise */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              '&:hover': { opacity: 0.9 }
            }}
            onClick={() => navigate('/dashboard')}
          >
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 700,
                mr: { xs: 0, md: 4 },
              }}
            >
              {user?.entreprise || 'Sersal'}
            </Typography>
          </Box>

          {/* Navigation Desktop (Tabs horizontales) */}
          {!isMobile && (
            <Tabs
              value={getNavigationValue()}
              onChange={(event, newValue) => handleNavigate(newValue)}
              sx={{
                flexGrow: 1,
                '& .MuiTabs-indicator': {
                  backgroundColor: theme.palette.secondary.main,
                  height: 3,
                },
                '& .MuiTab-root': {
                  color: 'white',
                  opacity: 0.7,
                  minHeight: 64,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  transition: 'all 0.3s',
                  '&:hover': {
                    opacity: 1,
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  },
                  '&.Mui-selected': {
                    color: 'white',
                    opacity: 1,
                    fontWeight: 600,
                  },
                },
              }}
            >
              <Tab
                icon={<HomeOutlined sx={{ fontSize: 20, mb: 0.5 }} />}
                iconPosition="start"
                label="Accueil"
                value="home"
              />
              <Tab
                icon={<PeopleOutlined sx={{ fontSize: 20, mb: 0.5 }} />}
                iconPosition="start"
                label="Clients"
                value="clients"
              />
              <Tab
                icon={<WorkOutlineOutlined sx={{ fontSize: 20, mb: 0.5 }} />}
                iconPosition="start"
                label="Projets"
                value="projets"
              />
              <Tab
                icon={<DescriptionOutlined sx={{ fontSize: 20, mb: 0.5 }} />}
                iconPosition="start"
                label="Devis"
                value="devis"
              />
              <Tab
                icon={<ReceiptOutlined sx={{ fontSize: 20, mb: 0.5 }} />}
                iconPosition="start"
                label="Factures"
                value="factures"
              />
              <Tab
                icon={
                  hasPremiumAccess ? (
                    <AccountBalanceWallet sx={{ fontSize: 20, mb: 0.5 }} />
                  ) : (
                    <Badge
                      badgeContent={<StarOutline sx={{ fontSize: 10 }} />}
                      sx={{
                        '& .MuiBadge-badge': {
                          backgroundColor: '#F59E0B',
                          color: 'white',
                          right: -2,
                          top: 2,
                          border: '2px solid ' + theme.palette.primary.main,
                          padding: '0 2px',
                          minWidth: 16,
                          height: 16,
                        },
                      }}
                    >
                      <AccountBalanceWallet sx={{ fontSize: 20, mb: 0.5, opacity: 0.7 }} />
                    </Badge>
                  )
                }
                iconPosition="start"
                label="Dépenses"
                value="expenses"
                sx={
                  !hasPremiumAccess
                    ? {
                        background: 'linear-gradient(90deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%)',
                        '&:hover': {
                          background: 'linear-gradient(90deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.15) 100%)',
                        },
                      }
                    : {}
                }
              />
            </Tabs>
          )}

          {/* Actions utilisateur (Desktop) */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ProfileMenu />
            <IconButton
              color="inherit"
              onClick={handleLogout}
              sx={{
                '&:hover': {
                  backgroundColor: theme.palette.secondary.main + '20',
                }
              }}
            >
              <LogoutOutlined />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Spacer for AppBar */}
      <Toolbar />

      {/* Main content */}
      <Container
        sx={{
          mt: 2,
          mb: 2,
          maxWidth: { xs: '100%', md: 'lg', lg: 'xl' },
        }}
      >
        {/* Subscription Banner */}
        <SubscriptionBanner />

        <Outlet />
      </Container>

      {/* Bottom Navigation (Mobile uniquement) */}
      {isMobile && (
        <Paper
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            borderTop: `2px solid ${theme.palette.secondary.main}`,
            zIndex: theme.zIndex.appBar,
          }}
          elevation={8}
        >
          <BottomNavigation
            value={getNavigationValue()}
            onChange={(event, newValue) => handleNavigate(newValue)}
            sx={{
              height: 70,
              backgroundColor: 'white',
              '& .MuiBottomNavigationAction-root': {
                minWidth: 'auto',
                padding: '6px 0',
                color: theme.palette.text.secondary,
              },
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.75rem',
                marginTop: '4px',
              },
              '& .Mui-selected': {
                color: theme.palette.secondary.main,
                fontWeight: 600,
              },
              '& .Mui-selected .MuiSvgIcon-root': {
                transform: 'scale(1.1)',
                transition: 'transform 0.2s',
              },
            }}
          >
            <BottomNavigationAction
              label="Accueil"
              value="home"
              icon={<HomeOutlined />}
            />
            <BottomNavigationAction
              label="Clients"
              value="clients"
              icon={<PeopleOutlined />}
            />
            <BottomNavigationAction
              label="Projets"
              value="projets"
              icon={<WorkOutlineOutlined />}
            />
            <BottomNavigationAction
              label="Devis"
              value="devis"
              icon={<DescriptionOutlined />}
            />
            <BottomNavigationAction
              label="Factures"
              value="factures"
              icon={<ReceiptOutlined />}
            />
            {/* Menu Dépenses - Avec badge Premium pour inciter à l'upgrade */}
            <BottomNavigationAction
              label={hasPremiumAccess ? "Dépenses" : "Dépenses"}
              value="expenses"
              icon={
                hasPremiumAccess ? (
                  <AccountBalanceWallet />
                ) : (
                  <Badge
                    badgeContent={<StarOutline sx={{ fontSize: 12 }} />}
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: '#F59E0B',
                        color: 'white',
                        right: -3,
                        top: 3,
                        border: '2px solid white',
                        padding: '0 2px',
                        minWidth: 18,
                        height: 18,
                      },
                    }}
                  >
                    <AccountBalanceWallet sx={{ opacity: 0.7 }} />
                  </Badge>
                )
              }
              sx={
                !hasPremiumAccess
                  ? {
                      '& .MuiBottomNavigationAction-label': {
                        fontSize: '0.70rem',
                        fontWeight: 600,
                        background: 'linear-gradient(90deg, #F59E0B 0%, #D97706 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      },
                    }
                  : {}
              }
            />
          </BottomNavigation>
        </Paper>
      )}

      {/* Dialog discrète pour informer des fonctionnalités de gestion comptable Premium */}
      <Dialog
        open={expensesDialogOpen}
        onClose={() => setExpensesDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#374151' }}>
          Gestion Comptable Premium
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2, color: '#6B7280' }}>
            La gestion des dépenses et de la comptabilité est une fonctionnalité réservée aux abonnés Premium.
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>
            Avec l'offre Premium, vous bénéficiez d'une comptabilité complète et automatisée :
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2, color: '#374151' }}>
            <li>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Suivi des dépenses</strong> - Enregistrez et catégorisez toutes vos dépenses professionnelles
              </Typography>
            </li>
            <li>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Justificatifs de dépenses</strong> - Téléchargez et stockez vos reçus et factures (photos, PDF)
              </Typography>
            </li>
            <li>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Rapports comptables automatisés</strong> - Génération de rapports mensuels, trimestriels et annuels
              </Typography>
            </li>
            <li>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Analyse de marges par projet</strong> - Comparez vos revenus et dépenses par chantier
              </Typography>
            </li>
            <li>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Export comptable</strong> - Exportez vos données en CSV, PDF ou ZIP avec tous les justificatifs
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Calcul automatique de la TVA</strong> - Rapports TVA collectée et déductible
              </Typography>
            </li>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setExpensesDialogOpen(false)} color="inherit">
            Fermer
          </Button>
          <Button
            onClick={() => {
              setExpensesDialogOpen(false);
              navigate('/pricing');
            }}
            variant="contained"
            sx={{
              bgcolor: '#F59E0B',
              '&:hover': { bgcolor: '#D97706' },
            }}
          >
            Voir les offres Premium
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Layout;
